import {
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { SwalPortalTargets } from '@sweetalert2/ngx-sweetalert2';
import { Router } from '@angular/router';
import { CurrentReservationService } from '@/common/services/reservation/current';
import { updateReservation } from '@/core/reservation/store/reservation.actions';
import { sendCartStep1UseCase } from '@/core/domain/cart-step1/usecases/sendCartStep1.usecase';
import { catchError, first, of, switchMap, take } from 'rxjs';
import { selectReservation } from '@/core/reservation/store/reservation.selector';
import { CartStep1Interface } from '@/core/domain/cart-step1/models/cart-step1.model';
import { v4 as uuidv4 } from 'uuid';
import {
    Discount,
    Features,
    isFeatureFlags,
    isFeatureList,
    Vehicle,
    VehicleFeature,
} from '@/core/domain/branches/models/branch.model';
import { isValid } from '@/common/utils/validation/common.utils';
import { UrlService } from '@/common/services/url/url.service';
import { AlertService } from '@/common/services/alert/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { GtmSelectCartUseCase } from '@/core/domain/gtm/usecases/gtmSelectCar.usecase';
import { FormatNumberPipe } from '@/common/pipes/formatNumber/format-number.pipe';

@Component({
    selector: 'fe-card-vehicle',
    templateUrl: './card-vehicle.component.html',
    styleUrls: ['./card-vehicle.component.scss'],
    providers: [FormatNumberPipe],
})
export class CardVehicleComponent implements OnInit {
    @Input() vehicle!: Vehicle;
    @Input() discountOffice: Discount | null | undefined;
    @Input() discount: Discount | null | undefined;
    @Input() discountPrepaid: Discount | null | undefined;

    @Input() rentalDays: number | undefined;
    @Input() international: boolean | undefined;

    @HostBinding('class.card-vehicle--int')
    get isInternationalCard(): boolean {
        return !!this.international;
    }

    @Input() rateWeekly: boolean | undefined;
    @Input() currency: string | null | undefined;

    @Output() detailOpened = new EventEmitter<CardVehicleComponent>();

    @ViewChild('accordionDetail')
    private readonly accordionDetail?: ElementRef<HTMLElement>;

    public detailMaxHeight: number | null = null;
    public currentLang = '';

    showDetail = false;
    isLoading = true;

    public filtered = false;

    public loadingBtnOffice = false;
    public loadingBtnPrepaid = false;

    constructor(
        private readonly router: Router,
        private readonly store: Store,
        public readonly swalTargets: SwalPortalTargets,
        private readonly sendCartStep1UseCase: sendCartStep1UseCase,
        public reservation: CurrentReservationService,
        private readonly url: UrlService,
        private readonly alert: AlertService,
        private readonly translate: TranslateService,
        private readonly gtmSelectCart: GtmSelectCartUseCase,
        private readonly formatNumber: FormatNumberPipe,
    ) {}

    public get savingsDaysKey(): string {
        return this.rentalDays === 1
            ? 'fe_step2_card_vehicle_p_offer_price_day'
            : 'fe_step2_card_vehicle_p_offer_price_days';
    }

    public get inventoryKey(): string {
        return this.vehicle.inventory === 1
            ? 'fe_step2_card_vehicle_inventory_singular'
            : 'fe_step2_card_vehicle_inventory_plural';
    }

    public getSavingsParams(method: 'office' | 'prepaid'): {
        amount: string;
        days: number | string;
    } {
        const charges = this.vehicle.charges[method];
        const savings = charges?.totalDiscount ?? charges?.discount;
        return {
            amount: `${this.formatNumber.transform(savings)}`,
            days: this.rentalDays ?? '',
        };
    }

    ngOnInit(): void {
        this.currentLang =
            this.translate.currentLang || this.translate.getDefaultLang();
    }

    public showDetails(): void {
        this.showDetail = !this.showDetail;

        if (!this.showDetail) {
            this.detailMaxHeight = null;
            return;
        }

        this.detailOpened.emit(this);
        setTimeout(() => {
            this.detailMaxHeight =
                this.accordionDetail?.nativeElement.scrollHeight ?? null;
        });
    }

    public closeDetail(): void {
        this.showDetail = false;
        this.detailMaxHeight = null;
    }

    @HostListener('window:resize')
    public onWindowResize(): void {
        if (!this.showDetail) {
            return;
        }

        this.detailMaxHeight =
            this.accordionDetail?.nativeElement.scrollHeight ?? null;
    }

    getOfficeTaxCharge(vehicle: Vehicle, index: number): number {
        return vehicle?.charges?.office?.taxes?.[index]?.charge ?? 0;
    }
    getOfficeTaxCurrency(vehicle: Vehicle, index: number): string {
        return vehicle?.charges?.office?.taxes?.[index]?.currency ?? 'MXN';
    }

    getPrepaidTaxCharge(vehicle: Vehicle, index: number): number {
        return vehicle?.charges?.prepaid?.taxes?.[index]?.charge ?? 0;
    }

    getTotalTaxCharges(vehicle: Vehicle): string {
        if (
            !vehicle.charges ||
            !vehicle.charges.office ||
            !vehicle.charges.office.taxes
        ) {
            return '0.00';
        }

        const total = vehicle.charges.office.taxes.reduce(
            (total, tax) => total + tax.charge,
            0,
        );

        return total.toFixed(2);
    }

    public onclickPrepago(typeOtion: boolean, vehicle: Vehicle): void {
        const uniqueId = uuidv4();
        this.store.dispatch(
            updateReservation({
                value: {
                    vehicle: this.vehicle,
                    type: this.discountOffice ?? this.discountPrepaid,
                    flowType: typeOtion ? 'prepaid' : 'office',
                    uuid: uniqueId,
                    insurances: [],
                    currency: this.getOfficeTaxCurrency(vehicle, 0),
                    isUpgradePromotion:
                        this.discount?.availableUpgrade &&
                        this.vehicle.id === this.discount?.originCategory,
                },
            }),
        );

        if (typeOtion) {
            this.loadingBtnPrepaid = true;
        } else {
            this.loadingBtnOffice = true;
        }
        this.store
            .select(selectReservation)
            .pipe(
                take(1),
                switchMap((reservation) => {
                    if (this.international) {
                        return of(true);
                    }
                    const cartData: CartStep1Interface = {
                        sessionId: uniqueId,
                        startDate: reservation.startDate,
                        endDate: reservation.endDate,
                        branchId: reservation.locationSelected,
                        isPrePaid: typeOtion,
                        currency: this.vehicle.charges.office.currency,
                        isInternational: this.international,
                    };

                    if (this.international) {
                        cartData.categoryCode = this.vehicle.code;
                    } else {
                        cartData.carId = reservation.vehicle.id;
                    }

                    if (isValid(reservation.differentLocationSelected)) {
                        cartData.returnBranchId =
                            reservation.differentLocationSelected;
                    }

                    if (reservation.discountCode) {
                        cartData.couponCode = reservation.discountCode;
                    }

                    if (reservation.age) {
                        cartData.age = reservation.age;
                    }

                    if (reservation.isUpgradePromotion) {
                        cartData.isUpgradePromotion =
                            reservation.isUpgradePromotion;
                        cartData.carIdUpgradePromotion =
                            reservation.upgradeCatergoryPromotion.id;
                    }

                    return this.sendCartStep1UseCase.execute(cartData).pipe(
                        switchMap((response) => {
                            this.store.dispatch(
                                updateReservation({
                                    value: {
                                        total: response.data?.amount,
                                    },
                                }),
                            );
                            return of(true);
                        }),
                        catchError((error) => {
                            this.alert.openDefault(error);
                            return of(null);
                        }),
                    );
                }),
            )
            .subscribe((isSuccessful) => {
                if (typeOtion) {
                    this.loadingBtnPrepaid = false;
                } else {
                    this.loadingBtnOffice = false;
                }
                if (isSuccessful) {
                    this.gtmSelectCart.execute().subscribe();
                    this.router.navigate([
                        this.url.format('reservations/trip-enhancements'),
                    ]);
                } else {
                    console.error('Navigation cancelled due to an error.');
                }
            });
    }

    get featureList(): VehicleFeature[] {
        return isFeatureList(this.vehicle?.features)
            ? this.vehicle.features
            : [];
    }

    get featureFlags(): Features | null {
        return isFeatureFlags(this.vehicle?.features)
            ? this.vehicle.features
            : null;
    }

    get hasFeatureList(): boolean {
        return this.featureList.length > 0;
    }

    get featureIcons(): VehicleFeature[] {
        return this.featureList.filter((feature) => !!feature.img);
    }
}
